import { Box, Text, VStack } from '@chakra-ui/react'

import { useColorModeValue } from '@/components/ui/color-mode'
import { MobileBottomNavItem as MobileBottomNavItemType } from '@/hooks/useMobileBottomNav'
import { Link } from '@/routing'

interface Props {
  item: MobileBottomNavItemType
  isActive: boolean
  /** Called when the special "Menu" tab (no route) is tapped. */
  onMenuClick?: () => void
}

export const MobileBottomNavItem = ({ item, isActive, onMenuClick }: Props) => {
  const activeColor = useColorModeValue('gray.900', 'gray.50')
  const defaultColor = useColorModeValue('gray.500', 'gray.400')
  // Yellow "tab pill" behind the active icon (design: --tab-pill).
  const activePill = useColorModeValue('brand.100', 'whiteAlpha.200')

  const IconComponent = item.icon
  const color = isActive ? activeColor : defaultColor

  const content = (
    <VStack gap="3px" py={1}>
      <Box
        w="56px"
        h="30px"
        borderRadius="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg={isActive ? activePill : 'transparent'}
        color={color}
        transition="background 0.15s ease"
      >
        <IconComponent size={22} />
      </Box>
      <Text
        fontSize="11px"
        fontWeight={isActive ? 'bold' : 'medium'}
        color={color}
        textAlign="center"
        lineHeight={1.2}
        letterSpacing="-0.01em"
      >
        {item.label}
      </Text>
    </VStack>
  )

  if (item.isMenu) {
    return (
      <Box
        role="button"
        tabIndex={0}
        onClick={onMenuClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onMenuClick?.()
          }
        }}
        cursor="pointer"
        aria-label={item.label}
        aria-current={isActive ? 'page' : undefined}
        display="flex"
        flex={1}
        minH="56px"
        justifyContent="center"
        alignItems="center"
      >
        {content}
      </Box>
    )
  }

  return (
    <Link
      to={item.to ?? '/'}
      aria-current={isActive ? 'page' : undefined}
      style={{
        display: 'flex',
        flex: 1,
        minHeight: '56px',
        justifyContent: 'center',
        alignItems: 'center',
        textDecoration: 'none',
      }}
    >
      {content}
    </Link>
  )
}
